// use-panel-rooms.jsx — HA-aware drop-in replacement for useRoomState.
//
// Returns the same shape as shared.jsx's useRoomState. For rooms with a
// MAPPING entry, it overlays live HA entity state on top of the ROOMS seed
// (and writes to HA via hass.callService on update). Rooms without a
// mapping stay on mock data — letting us ship Office wired-up while the
// other rooms remain visual placeholders until their entity IDs are
// audited.

import * as React from 'react';
import { ROOMS } from '../shared.jsx';
import { capVolume } from '../music/volume-drag.js';

// All Sonos speakers in the Wallace St ecosystem. Sourced from
// design/HANDOFF.md §Rooms + §Speakers — the curated household list.
// Order: room speakers (full ATile cards) first, then standalone
// speakers (kids' rooms, guest, laundry, desk, portables). Used by
// the Sonos modal's grouping UI; tapping any one (other than the
// current room) joins/leaves the group via media_player.join/unjoin.
// Exported so pwa/watch-set.js can include these in the render filter.
// Eight of them (the Play:1s, Desk, Turntable, Move, Roam) appear
// nowhere in MAPPINGS, so a filter built from MAPPINGS alone would let
// the Sonos speaker list go stale.
export const SONOS_SPEAKERS = [
  { entity: 'media_player.office',          name: 'Office',         kind: 'arc',        roomId: 'office' },
  { entity: 'media_player.pavilion',        name: 'Pavilion',       kind: 'port',       roomId: 'pavilion' },
  { entity: 'media_player.rumpus',          name: 'Rumpus',         kind: 'connect',    roomId: 'rumpus' },
  { entity: 'media_player.master_bed',      name: 'Master',         kind: 'connectamp', roomId: 'master_bed' },
  { entity: 'media_player.ensuite',         name: 'Ensuite',        kind: 'connectamp', roomId: 'master_ensuite' },
  { entity: 'media_player.pool',            name: 'Pool',           kind: 'connect',    roomId: 'pool' },
  { entity: 'media_player.connor_s_room',   name: "Connor's Room",  kind: 'play1' },
  { entity: 'media_player.dylans_room',     name: "Dylan's Room",   kind: 'play1' },
  { entity: 'media_player.guest_bedroom',   name: 'Guest',          kind: 'play1' },
  { entity: 'media_player.laundry',         name: 'Laundry',        kind: 'play1' },
  { entity: 'media_player.desk',            name: 'Office Desk',    kind: 'playbar' },
  { entity: 'media_player.office_turntable',name: 'Turntable',      kind: 'port' },
  { entity: 'media_player.move',            name: 'Move',           kind: 'portable' },
  { entity: 'media_player.roam_2',          name: 'Roam 2',         kind: 'portable' },
];

// ────────────────────────────────────────────────────────────────────────────
// HA entity mappings per room.
//
// Conventions for this object:
//
//   lights:  array of { id, entity }. `id` MUST match the corresponding
//            light id in shared.jsx ROOMS. One UI fixture = one HA entity.
//
//   blinds:  string OR string[]. If array, the slider averages positions
//            on read and broadcasts the new position to all entities on
//            write. (Used for the Office's 3-blackout window set.)
//
//   louvre:  string OR string[]. Same semantics as blinds, for
//            horizontal louvre slats (rotated by angle, % maps to
//            tilt). Pavilion has one (the "pool louvre"); other rooms
//            may add more as they're audited.
//
//   sheer:   string OR string[]. Same semantics as blinds, for
//            second-layer sheer roller blinds (% open). Office has 3
//            of these behind its 3 blackouts. A room can have any
//            combination of blinds / louvre / sheer — they render as
//            separate rows in the Shades modal.
//
//   sonos:   string entity_id. Volume + play/pause are wired; track
//            metadata is read-only.
//
//   screen:  { tv, tvRemote, appleTv, appleTvRemote } — recorded for the
//            M3 Apple-Remote modal. Not yet consumed by the hook. Naming
//            convention from ../ha-av/HANDOFF.md (TV is canonical state,
//            Apple TV state/remote use `apple_tv_<room>` suffix).
//            `tvRemote` is nullable — Pavilion's LG has no remote entity
//            at all. Any future consumer must branch on it rather than
//            assume every room has one; brand dispatch already lives in
//            tv/remote-config.js and should stay the single place that
//            knows which vocabulary a given TV speaks.
//
//   av:      { watch, listen, off, extendToPool? }. `watch` / `listen`
//            / `off` are `script.*` ids ha-av exposes for coordinated
//            verbs — call them to wake TV/amp/Apple TV with the right
//            timing. `extendToPool` is different: it's an
//            `input_boolean.*` (not a script) — write the boolean to
//            engage / disengage, and ha-av's bi-directional sync drives
//            the underlying extend/unextend scripts. Not yet consumed
//            by the hook; recorded for the M3 activity-tile wire-up.
//
//   amp:     { main, zone2?, zone3? } — Onkyo amp media_players.
//            Pavilion uses zone2 for Dining audio routing. Rumpus uses
//            zone3 to drive the Pool array via the SR2-125 analog
//            input; the same drx_5_zone_3 entity is also recorded
//            under Pool for symmetry. Not yet consumed by the hook.
//
//   idle:    binary_sensor entity_id reporting "room AV is idle"
//            (TV off + Apple TV off/standby + Sonos not playing).
//            Drives ha-av's 30-min idle-shutdown automation; also a
//            useful "is this room awake?" indicator for the UI.
//            Pavilion + Rumpus only.
//
// UX constraint: the two `*_extend_pool` toggles (Pavilion + Rumpus)
// share `media_player.drx_5_zone_3` and **cannot be active
// simultaneously**. Toggling both mid-streams the DRX-5 main source,
// audio selector, and main volume — Pavilion's extend drops main
// volume to 0 and Rumpus's extend doesn't restore it. Treat them as
// mutually exclusive at the UI layer (turning one on must turn the
// other off first). See HANDOFF-ha-av.md §4 #13.
//
//   pavilionMode: { extendDining } — input_boolean toggling Kitchen→Both
//            mode on the Pavilion stack. Combined with amp.main +
//            amp.zone2 to derive the four-state listening mode. Pavilion
//            only.
//
//   climate: string entity_id, or null if the room legitimately has no
//            climate entity.
//
//   tempSensor / humiditySensor: optional sensor entity_ids giving a
//            read-only room temperature and humidity when climate is
//            null. Display only; humidity shows on the room page alone.
//            Where climate IS mapped, tempSensor replaces the unit's
//            current_temperature as the room's reading (the unit may
//            sense somewhere else); humiditySensor is not read there.
//
// What's confidently wired here vs TBD:
//
//   AV-side entities (sonos, screen, av scripts, amp) are taken directly
//   from ../ha-av/HANDOFF.md, which is the authoritative contract.
//   These are safe to wire without further audit.
//
//   Light / cover / climate entities are owned by sibling repos
//   (fibaro-transition, ha-office-blinds) and need a per-room HA audit
//   to confirm slugs. Office is fully audited; the others are TODO. To
//   audit, run:
//     set -a && source .env && set +a
//     python3 -c "import json,os,urllib.request as u; \
//       r=u.Request(os.environ['HA_URL']+'/api/states', headers={'Authorization':'Bearer '+os.environ['HA_TOKEN']}); \
//       [print(s['entity_id']) for s in json.loads(u.urlopen(r).read()) if '<room>' in s['entity_id'].lower()]"
//
//   Standalone Sonos units (kids' rooms, Guest, Laundry, portables,
//   Office's desk + turntable) don't live here — they're rendered on a
//   separate Speakers surface and bind to media_player.* directly.
// ────────────────────────────────────────────────────────────────────────────
export const MAPPINGS = {
  office: {
    // TV audio to other Sonos rooms (ha-av tv_to_sonos); read by tv/sonos-rooms.jsx.
    tvToSonos: 'sensor.tv_to_sonos_rooms',
    lights: [
      { id: 'lamp',       entity: 'light.office_lamp' },                    // Hue bulb from 2026-07-28; replaced Aeotec/Fibaro 735
      { id: 'downlights', entity: 'light.office_office_downlights_24' },   // Fibaro 24 — ceiling downlights
      { id: 'strip',      entity: 'light.office_desk_strip' },             // Fibaro FGRGBW-442 W channel, node 153 — under-shelf COB task light
    ],
    blinds: [
      'cover.office_blind_1',                                              // NE  window blackout
      'cover.office_blind_2',                                              // NNW window blackout
      'cover.office_blind_3',                                              // WNW window blackout
    ],
    sheer: [
      'cover.office_sheer_1',                                              // NE  window sheer
      'cover.office_sheer_2',                                              // NNW window sheer
      'cover.office_sheer_3',                                              // WNW window sheer
    ],
    // Sun-tracking day toggle (ha-blinds, 2026-07-12). Selects the
    // office daytime shade model: on = geometric sun-tracking (the
    // at-the-desk workday default, pill "Sun tracking"), off = the
    // automation idles and leaves the covers alone after the morning
    // open (the weekend / NSW-public-holiday default, pill "Idle";
    // ha-blinds calls the same model "rest open"). ha-blinds resets it to
    // the calendar default at 00:05, so a flip from the panel lasts
    // the rest of that day only; the position watchdog converges the
    // covers within ~1 min of a change. Orthogonal to
    // input_boolean.office_blinds_auto_enabled (full manual pause —
    // deliberately NOT surfaced here). Owner:
    // ha-blinds/packages/office_blinds.yaml.
    sunTracking: 'input_boolean.office_blinds_sun_tracking',
    sonos:   'media_player.office',                                        // Arc — TV Autoplay handles ARC source switching natively
    screen: {
      tv:            'media_player.office_tv',
      tvRemote:      'remote.office_tv',
      // Honest power read. `tv` above is androidtv_remote with
      // assumed_state — see the tvReal note on Rumpus for the whole
      // story and the measurements behind it.
      tvReal:        'media_player.office_tv_2',
      appleTv:       'media_player.apple_tv_office',
      appleTvRemote: 'remote.apple_tv_office',
      appleTvKeyboardFocus: 'binary_sensor.office_apple_tv_keyboard_focus',
    },
    audio: {
      surround:'number.office_surround_level',                                // Arc atmos surround mix
    },
    // EQ owner — the Arc IS the amplifier here, so Sonos tone is the
    // real control. See overlayEq for why tone lives apart from audio:.
    eq: {
      owner:   'Arc',
      bass:    'number.office_bass',
      treble:  'number.office_treble',
    },
    av: {
      watch:  'script.office_watch',
      // Office "listen" — no script; press play on the Arc and TV Autoplay does the rest.
      listen: null,
      off:    'script.office_off',
    },
    // Scenes — the ha-office-studio mode system, the first room whose
    // scene keys do anything (2026-10-29). Each key calls a script rather
    // than input_select.select_option, because selecting the option that
    // is already current is a no-op in HA and would leave a disturbed
    // room un-restored; the scripts re-trigger automation.office_apply_mode
    // in that case. `state` is honest (a helper, not assumed_state), so
    // the current mode is drawn chosen. Present is deliberately not here:
    // it seals the room and stays on the Stream Deck at the desk; while
    // it runs no key is drawn chosen. Relax sets lights only; TV and
    // music stay on the activity row. Done is end of work (strip and key
    // light off, room lights to the night look at night and off by day)
    // through the same script the deck's Work long-press calls.
    scenes: {
      state: 'input_select.office_mode',
      items: [
        { id: 'work',  label: 'Work',  script: 'script.office_mode_work'  },
        { id: 'call',  label: 'Call',  script: 'script.office_mode_call'  },
        { id: 'relax', label: 'Relax', script: 'script.office_mode_relax' },
        { id: 'done',  label: 'Done',  script: 'script.office_end_of_work' },
      ],
    },
    // Office and Master Bedroom share the same Heritage HVAC unit
    // (single split-system serving both via ducting). Same climate
    // entity bound on both rooms; changes from either tile drive
    // the shared unit.
    climate: 'climate.heritage',
    // Heritage senses return air in the hall, not this room. The Ecowitt
    // plant gateway's built-in sensor is the Office reading (Ben's call
    // 2026-10-28, no new sensor); it can read high on a sunny afternoon.
    tempSensor: 'sensor.gw1200c_indoor_temperature',
  },

  pavilion: {
    // TV audio to other Sonos rooms (ha-av tv_to_sonos); read by tv/sonos-rooms.jsx.
    tvToSonos: 'sensor.tv_to_sonos_rooms',
    // Light fixture inventory across the open-plan Pavilion area —
    // grouped into three zones by the `zone:` field below:
    //   • Kitchen  — pendants/island over the bench, ceiling
    //     downlights, pantry, and the back-window highlight strip.
    //   • Living   — main pendant, ceiling downlights, fireplace
    //     downlight, accent LED strip. (`light.lounge_lounge_room_lamp_737`
    //     was dropped — offline on the Z-Wave mesh.)
    //   • Dining   — chandelier (entity slug says `pendant`, friendly
    //     name in HA has drifted to "Lounge Uplight Dummy" — Fibaro
    //     HC2 rename collision worth cleaning up HA-side later),
    //     downlights, wall sconces, fireplace downlight, accent LED
    //     strip.
    // The Lights tile + modal group by `zone` for clarity.
    lights: [
      // Kitchen
      { id: 'kitchen-pendant',     zone: 'kitchen', entity: 'light.kitchen_kitchen_pendant_204' },
      { id: 'kitchen-island',      zone: 'kitchen', entity: 'light.kitchen_kitchen_island_199' },
      { id: 'kitchen-downlights',  zone: 'kitchen', entity: 'light.kitchen_kitchen_downlights_335' },
      { id: 'kitchen-pantry',      zone: 'kitchen', entity: 'light.kitchen_pantry_downlights_194' },
      { id: 'kitchen-highlights',  zone: 'kitchen', entity: 'light.backyard_kitchen_highlights_579' },
      // Living
      { id: 'living-pendant',      zone: 'living',  entity: 'light.unknown_lounge_pendant_119' },
      { id: 'living-downlights',   zone: 'living',  entity: 'light.lounge_lounge_room_downligh_214' },
      { id: 'living-fire',         zone: 'living',  entity: 'light.lounge_lounge_room_fire_lig_219' },
      { id: 'living-strip',        zone: 'living',  entity: 'light.lounge_lounge_room_strip_589' },
      // Dining
      { id: 'dining-chandelier',   zone: 'dining',  entity: 'light.dining_dining_room_pendant_224' },
      { id: 'dining-downlights',   zone: 'dining',  entity: 'light.dining_dining_room_downligh_244' },
      { id: 'dining-sconces',      zone: 'dining',  entity: 'light.dining_dining_room_wall_lig_234' },
      { id: 'dining-fire',         zone: 'dining',  entity: 'light.dining_dining_room_fire_li_229' },
      { id: 'dining-strip',        zone: 'dining',  entity: 'light.dining_dining_room_strip_596' },
    ],
    // Shades: design/HANDOFF.md specifies 6 blinds (Kitchen + Living)
    // + 1 blind (Dining) in two modal groups. Current data shape only
    // supports a single average — all 7 are bundled here for now;
    // when the Shades modal gains a two-group layout, split into
    // `blindsKitchenLiving` + `blindsDining` (and extend the hook).
    blinds: [
      'cover.kitchen_kitchen_blind_632',                                   // Kitchen
      'cover.lounge_blinds_two_435',                                       // Living 2
      'cover.lounge_blinds_three_414',                                     // Living 3
      'cover.lounge_blinds_four_432',                                      // Living 4
      'cover.lounge_blinds_five_426',                                      // Living 5
      'cover.lounge_blinds_six_417',                                       // Living 6
      'cover.dining_blinds_1_429',                                         // Dining
    ],
    // Three motorised louvres in the Pavilion's open-plan area. All
    // are driven by Fibaro pulse-pairs (a momentary "close" switch and
    // a momentary "open" switch). Only the Pool louvre is also wrapped
    // in a `cover.*` entity that tracks open/closed state; High and
    // West have no state surface in HA, so their "current state" is
    // tracked locally per session in the Shades tile / modal.
    louvres: [
      { id: 'high', name: 'High',
        openSwitch:    'switch.dining_high_louvre_open_366',
        closeSwitch:   'switch.dining_high_louvre_close_364',
        stateBoolean:  'input_boolean.pavilion_louvre_high_open' },
      { id: 'pool', name: 'Pool',
        openSwitch:    'switch.dining_pool_louvre_open_372',
        closeSwitch:   'switch.dining_pool_louvre_close_370',
        stateBoolean:  'input_boolean.pavilion_louvre_pool_open',
        coverEntity:   'cover.lounge_blinds_pool_louvre_420' },
      { id: 'west', name: 'West',
        openSwitch:    'switch.dining_west_louvre_open_395',
        closeSwitch:   'switch.dining_west_louvre_close_393',
        stateBoolean:  'input_boolean.pavilion_louvre_west_open' },
    ],
    sonos:   'media_player.pavilion',                                      // Port → DRX-3.1 CD/TV-CD input
    screen: {
      tv:            'media_player.pavilion_tv',                           // LG OLED — no _cast surface (predates Chromecast)
      // NO REMOTE ENTITY EXISTS. webostv exposes none, so the plausible
      // `remote.pavilion_tv` this used to name is a ghost — audited
      // 2026-07-29 against live HA and it is the only missing id of the
      // 111 this file references. Held as null rather than deleted so
      // the shape still documents itself and any consumer is forced to
      // branch instead of silently no-opping into a non-existent entity.
      // Pavilion button dispatch goes via `webostv.button` against the
      // media_player above; see tv/remote-config.js, which already does
      // this correctly.
      tvRemote:      null,
      appleTv:       'media_player.apple_tv_pavilion',
      appleTvRemote: 'remote.apple_tv_pavilion',
      appleTvKeyboardFocus: 'binary_sensor.pavilion_apple_tv_keyboard_focus',
    },
    audio: {
      centre:  'input_number.drx_3_1_centre_level',                           // DRX-3.1 centre channel level (dB)
    },
    // EQ owner is the amp, not the Sonos. Pavilion's Sonos is a Port on
    // analog line-out, so `number.pavilion_bass` shapes Sonos music only
    // and does nothing to TV audio, which reaches the DRX-3.1 by another
    // input. The amp's tone stage covers both. Helpers are driven by
    // ha-av's drx_3_1 bass/treble apply automations (raw eISCP TFR).
    eq: {
      owner:   'DRX-3.1',
      bass:    'input_number.drx_3_1_bass',
      treble:  'input_number.drx_3_1_treble',
    },
    av: {
      watch:        'script.pavilion_watch',
      listen:       'script.pavilion_listen',                              // Kitchen mode (default)
      listenDining: 'script.pavilion_listen_dining',                       // Dining-only mode
      off:          'script.pavilion_off',                                 // Whole stack (also drops zone 2)
      // Toggle (input_boolean) — write to engage / disengage. ha-av's
      // bi-directional sync drives the underlying extend/unextend
      // scripts (which force DRX-5 BD/DVD audio_selector to ANALOG via
      // eISCP SLA02, drop DRX-5 main volume to 0, and turn on zone 3).
      // **Mutually exclusive with rumpus.av.extendToPool** — both ride
      // drx_5_zone_3. UI must enforce: turning this on requires
      // turning Rumpus's extension off first. SR2-125 also requires
      // Pool Sonos to be silent (TOSLINK priority over analog input).
      extendToPool: 'input_boolean.pavilion_extend_pool',
    },
    idle: 'binary_sensor.pavilion_av_idle',                                // TV off + Apple TV off/standby + Sonos not playing
    amp: {
      main:  'media_player.drx_3_1',                                       // TV Wall + TV Centre + Kitchen ceilings
      zone2: 'media_player.drx_3_1_zone_2',                                // Dining ceilings
    },
    // Source-picker note: do NOT expose `VIDEO2 ··· CBL/SAT` as a
    // source option on DRX-3.1. It's wired to the Blustream HDMI
    // matrix output, but the matrix is dormant (all 4 inputs
    // physically empty, confirmed 2026-05-13) and would silently
    // produce no audio.
    pavilionMode: {
      extendDining: 'input_boolean.pavilion_extend_dining',                // Toggles Kitchen → Both. Only meaningful when main is on.
    },
    // Destinations chips — replaces the per-zone-toggle scattering of
    // "Extend Dining" / "Extend Pool" booleans with a single "Playing
    // in" surface. See overlayDestinations + destinationsCommand for
    // the underlying state-machine handling.
    destinations: ['kitchen', 'dining', 'pool'],
    // 4 climate entities exist: climate.pavilion_aircon (whole-area
    // master) + climate.pavilion_kitchen + climate.pavilion_living +
    // climate.pavilion_dining. Single-binding for now; modal can
    // expose sub-zones in a later UI iteration.
    // The master was `climate.pavilion` until 2026-07-13: it is a YAML
    // climate_group with no unique_id, so renaming it "Pavilion" →
    // "Pavilion Aircon" moved the entity_id too.
    climate: 'climate.pavilion_aircon',
    climateSubZones: {
      kitchen: 'climate.pavilion_kitchen',
      living:  'climate.pavilion_living',
      dining:  'climate.pavilion_dining',
    },
  },

  rumpus: {
    // TV audio to other Sonos rooms (ha-av tv_to_sonos); read by tv/sonos-rooms.jsx.
    tvToSonos: 'sensor.tv_to_sonos_rooms',
    lights: [
      { id: 'front-downlights', entity: 'light.rumpus_rumpus_fron_downligh_254' },   // Fibaro naming truncates "front" → "fron"
      { id: 'rear-downlights',  entity: 'light.rumpus_rumpus_rear_downligh_259' },
      { id: 'hall-downlights',  entity: 'light.rumpus_rumpus_hall_downligh_269' },
      { id: 'rear-strip',       entity: 'light.rumpus_rumpus_rear_strip_313' },
      { id: 'stair-strip',      entity: 'light.rumpus_rumpus_stair_strip_260' },
    ],
    // No Rumpus blinds found in HA. One motorised louvre exists,
    // controlled by the same Fibaro open/close pulse-pair pattern as
    // Pavilion's louvres (no cover.* wrapper for state).
    louvres: [
      { id: 'rumpus', name: 'Louvre',
        openSwitch:    'switch.rumpus_rumpus_louvre_open_302',
        closeSwitch:   'switch.rumpus_rumpus_louvre_close_300',
        stateBoolean:  'input_boolean.rumpus_louvre_open' },
    ],
    sonos:   'media_player.rumpus',                                        // Connect → DRX-5 CD/TV-CD input
    screen: {
      tv:            'media_player.rumpus_tv',                             // Sony BRAVIA (clean BraviaSync CEC)
      tvRemote:      'remote.rumpus_tv',
      // Honest power read, and the only entity in this room that
      // reports a source.
      //
      // `tv` above is the androidtv_remote entity and it carries
      // `assumed_state: true`: it records the state HA *asked* for, in
      // the same event as the service call. It also rests at `off` when
      // the TV is genuinely asleep, never `unavailable` — verified
      // 2026-09-27 against live state and seven days of history, which
      // is the same wrong premise that had script.rumpus_watch skipping
      // its own WoL step (see ha-av HANDOFF bite 19).
      //
      // Consequence for this panel: switch the TV off at the Sony remote
      // and `tv` can hold `on`, so the room reads awake, Power sends
      // `off` when the user wanted `on`, and the two surfaces disagree
      // for the rest of the session. The braviatv entity below polls the
      // TV over Sony's REST API — measured 9.6 s to follow a wake on
      // 2026-09-27, slower than the echo but an actual read. It is also
      // the only one of the two that exposes `source`.
      tvReal:        'media_player.rumpus_tv_2',
      appleTv:       'media_player.apple_tv_rumpus',
      appleTvRemote: 'remote.apple_tv_rumpus',
      appleTvKeyboardFocus: 'binary_sensor.rumpus_apple_tv_keyboard_focus',
    },
    audio: {
      centre:  'input_number.drx_5_centre_level',                             // DRX-5 centre channel level (dB)
    },
    // EQ owner is the amp, and here there is no alternative: the Sonos
    // Connect reaches the DRX-5 over TOSLINK, and Sonos tone acts on the
    // analog output stage, so `number.rumpus_bass` is not in this signal
    // path at all. Driven by ha-av's drx_5 bass/treble apply automations.
    eq: {
      owner:   'DRX-5',
      bass:    'input_number.drx_5_bass',
      treble:  'input_number.drx_5_treble',
    },
    av: {
      watch:  'script.rumpus_watch',
      listen: 'script.rumpus_listen',
      off:    'script.rumpus_off',
      // Toggle — same shape as pavilion.extendToPool. Sends whatever
      // DRX-5 main is reproducing (BRAVIA ARC via TV, or Rumpus Sonos
      // via CD) to zone 3 → SR2-125 → Pool. Main source/volume left
      // alone (unlike Pavilion's, which forces source + zeroes main
      // volume). **Mutually exclusive with pavilion.av.extendToPool**
      // — UI must enforce. Pool Sonos must be silent for the SR2-125
      // to fall back from TOSLINK priority to its analog input.
      extendToPool: 'input_boolean.rumpus_extend_pool',
    },
    idle: 'binary_sensor.rumpus_av_idle',                                  // TV off + Apple TV off/standby + Sonos not playing
    amp: {
      main:  'media_player.drx_5',
      // drx_5_zone_2 is an orphan (pre-out unconnected) — don't surface.
      zone3: 'media_player.drx_5_zone_3',                                  // drives Pool via SR2-125; verified end-to-end 2026-05-13.
    },
    // Destinations chips — Rumpus has one local zone, plus optional
    // Pool extension via DRX-5 zone 3.
    destinations: ['rumpus', 'pool'],
    // The amp-menu "Zone 3 Output Level" (raised from 17 → 55 during
    // the 2026-05-13 trace) gates the zone 3 pre-out and is invisible
    // to HA. Don't assume the HA volume slider on drx_5_zone_3 is the
    // only volume control — if Pool is too quiet, the buried amp menu
    // is the next thing to check.
    //
    // Source-picker note: DRX-5 HDMI 1 is wired to the dormant
    // Blustream matrix output — don't expose any DRX-5 HDMI source in
    // a picker UI. Valid sources are `TV` (BRAVIA ARC) and
    // `CD ··· TV/CD` (Sonos Connect line-in).
    climate: null,                                                          // No climate.rumpus in HA (audited 2026-05-25 — HA exposes only heritage, pavilion[+sub-zones], guest)
    tempSensor: 'sensor.rumpus_temp_818',                                  // HC3 temp/humidity sensor; not the dehumidifier's unit temperature
    humiditySensor: 'sensor.rumpus_humidity_819',
  },

  master_bed: {
    // The walk-in robe (`light.robe_*`) is treated as part of Master
    // Bedroom and rendered under its own subhead. Bedside lamps live
    // behind a smart-plug `switch.*` rather than a `light.*` — on/off
    // only, no brightness; the lights overlay + updateLight branch on
    // entity domain to handle this.
    lights: [
      { id: 'downlights',     zone: 'bedroom',  entity: 'light.master_suite_master_downlights_32' },
      { id: 'bedside',        zone: 'bedroom',  entity: 'switch.master_bedside_lamps' },
      { id: 'wardrobe',       zone: 'wardrobe', entity: 'light.robe_robe_downlights_515' },
      { id: 'wardrobe-strip', zone: 'wardrobe', entity: 'light.robe_robe_strip_545' },
    ],
    // Utilities — non-fixture switched loads that render in the
    // LightsShell's "Utilities" card. See overlayUtilities below.
    utilities: [
      { id: 'robe-exhaust', name: 'Walk-in Robe Exhaust', entity: 'switch.master_bed_robe_exhaust', kind: 'fan' },
    ],
    // Master Bedroom has no blinds and no louvre (user confirmed
    // 2026-05-24, prior "shades exist" note was incorrect).
    blinds:  null,
    louvre:  null,
    sonos:   'media_player.master_bed',                                    // Connect:Amp → ceiling stereo pair
    // EQ owner — Connect:Amp is the amplifier, so Sonos tone is the
    // real control. No TV, no Onkyo amp: pure Sonos end to end.
    eq: {
      owner:   'Connect:Amp',
      bass:    'number.master_bed_bass',
      treble:  'number.master_bed_treble',
    },
    // No TV, no amp, no AV verb scripts — Sonos-only room.
    // Shares climate.heritage with Office — see office entry above.
    climate: 'climate.heritage',
    tempSensor: 'sensor.master_suite_temp_813',                             // the room itself; Heritage reads the hall
  },

  master_ensuite: {
    // Downlights + both strips are the fixture lights. The exhaust fan and
    // heated towel rail surface as `light.*` entities too (Fibaro
    // convention — switched loads come through under `light`) but
    // belong in the Utilities card, not the lights groups.
    lights: [
      { id: 'downlights', entity: 'light.ensuite_ensuite_downlights_510' },
      { id: 'strip',      entity: 'light.ensuite_ensuite_strip_538' },     // above the cabinet; dead node, installer
      { id: 'vanity-strip', entity: 'light.ensuite_under_vanity_strip' },  // below the cabinet, HC3 device 820, built 2026-09-26
    ],
    utilities: [
      { id: 'exhaust',    name: 'Exhaust Fan',       entity: 'switch.master_ensuite_exhaust',    kind: 'fan' },
      { id: 'towel-rail', name: 'Heated Towel Rail', entity: 'switch.master_ensuite_towel_rail', kind: 'radiator' },
    ],
    // `cover.bathroom_roller` confirmed as the Master Ensuite roller
    // blind 2026-05-25 by physical hardware test (fired set_position 15
    // via REST, user confirmed which window moved). device_class=blind,
    // supports open/close/set_position. Available now after being
    // `unavailable` on the mesh earlier.
    blinds: ['cover.bathroom_roller'],
    sonos:   'media_player.ensuite',                                       // Connect:Amp → ceiling stereo pair
    // EQ owner — Connect:Amp is the amplifier, so Sonos tone is the
    // real control. No TV, no Onkyo amp: pure Sonos end to end.
    eq: {
      owner:   'Connect:Amp',
      bass:    'number.ensuite_bass',
      treble:  'number.ensuite_treble',
    },
    climate: null,                                                          // No climate.ensuite in HA (re-audited 2026-05-25)
    // HC3 multisensor (motion, lux, temp). Reports in ~1° steps.
    tempSensor: 'sensor.ensuite_ensuite_temp_677',
  },

  pool: {
    // The original prototype seed had pool/deck/path; HA only exposes
    // two pool-area light entities (Pool Lights + Pool Wall Lights).
    // Path/deck lighting is on different circuits (frontyard /
    // east-garden) and isn't part of the Pool tile.
    lights: [
      { id: 'pool', entity: 'light.pool_pool_lights_293' },
      { id: 'wall', entity: 'light.pool_pool_wall_lights_573' },
    ],
    sonos:   'media_player.pool',                                          // TOSLINK → SR2-125 → pool array; no HA-side amp coordination of its own
    // No `eq:` on purpose. Pool's Connect reaches the SR2-125 over
    // optical, so Sonos tone (which acts on the analog output stage)
    // is not in the signal path, and the SR2-125 has no network
    // control. There is nothing here to drive — don't map this room
    // to number.pool_bass; the slider would move and do nothing.
    // The SR2-125 also has an analog RCA input fed from DRX-5 zone 3 —
    // the verified path for extending Pavilion or Rumpus AV to Pool.
    // Two ha-av toggles drive it:
    //   input_boolean.pavilion_extend_pool → DRX-5 BD/DVD source, main
    //     volume zeroed, zone 3 on (drx_5_zone_3 in MAIN SOURCE mode)
    //   input_boolean.rumpus_extend_pool   → DRX-5 main as-is, zone 3
    //     on (whatever Rumpus is reproducing flows through)
    // **Mutually exclusive** — they share drx_5_zone_3. See the
    // pavilion / rumpus entries for the UX constraint. Pool Sonos
    // must be silent (TOSLINK priority on SR2-125) for the extension
    // to be audible.
    amp: {
      zone3: 'media_player.drx_5_zone_3',                                  // shared with rumpus.amp.zone3
    },
  },
};

const { useState, useMemo, useCallback, useEffect, useRef } = React;

// Convert HA brightness (0-255) to UI percent (0-100).
const haBrightnessToPct = (b) =>
  b == null ? null : Math.round((b / 255) * 100);

// Convert UI percent (0-100) to HA brightness (0-255).
const pctToHaBrightness = (p) =>
  Math.max(0, Math.min(255, Math.round((p / 100) * 255)));

// ── Optimistic intent layer ──────────────────────────────────────────
//
// Every overlay* here reads HA as the single source of truth, which is
// correct but late. Until 2026-07-19 a light tap wrote optimistically
// into mockRooms and was then overwritten by the very next overlay
// pass, so the control snapped straight back to its old state until HA
// echoed — the "honest but late UI reads as *didn't register*"
// complaint. (The old comment on `update()` claiming the mock write
// served as optimistic UI for mapped rooms was simply wrong.)
//
// Intents are keyed by entity_id, the unit HA actually echoes, and win
// over the overlay until the echo lands or INTENT_TTL passes. TTL
// exists so a dropped Z-Wave command can't strand the UI showing a
// state the device never reached — better to fall back to the truth,
// late, than to lie indefinitely.
//
// Deliberately NOT used for covers: they travel for tens of seconds and
// report intermediate positions the whole way, so the live position IS
// the good feedback. Their in-flight signal is the entity's own
// opening/closing state — see overlayCoverMoving.
const INTENT_TTL = 10000;

// Has HA caught up with what we asked for? Brightness gets a ±2%
// tolerance because Z-Wave dimmers round-trip through 0-255 and rarely
// land on the exact percentage that was requested.
export function lightIntentSettled(intent, s) {
  if (!s) return false;
  if ((s.state === 'on') !== intent.on) return false;
  if (intent.brightness == null || !intent.on) return true;
  const actual = haBrightnessToPct(s.attributes?.brightness);
  return actual != null && Math.abs(actual - intent.brightness) <= 2;
}

// Apply a pending intent over the live HA values for one light/utility.
// `pending: true` is what the UI hangs its delayed spinner on.
function applyLightIntent(live, intent, s) {
  if (!intent || lightIntentSettled(intent, s)) return live;
  return {
    ...live,
    on: intent.on,
    brightness: live.brightness == null
      ? null                                   // switch.* — no brightness concept
      : (intent.brightness != null ? intent.brightness : live.brightness),
    pending: true,
  };
}

function overlayLights(seedLights, mapping, haStates, intents) {
  if (!mapping?.lights || !haStates) return seedLights;
  return seedLights.map((l) => {
    const m = mapping.lights.find((x) => x.id === l.id);
    if (!m) return l;
    // Always carry zone through from the mapping so UI can group/filter.
    const zoned = m.zone ? { ...l, zone: m.zone } : l;
    const s = haStates[m.entity];
    if (!s) return { ...zoned, available: false }; // entity not in HA yet — render as unavailable
    // `unavailable` / `unknown` states (Z-Wave mesh dropout, offline
    // device) → mark as unavailable so the tile can hide rather than
    // render a permanently-off chip that ignores taps. An intent over an
    // unavailable entity is pointless, so don't apply one.
    if (s.state === 'unavailable' || s.state === 'unknown') {
      return { ...zoned, available: false, on: false };
    }
    // switch.* entities (smart plugs running lamps) have no brightness
    // concept — render the chip with on/off only, no % readout, and
    // exclude from the master-brightness average upstream.
    const isSwitch = m.entity.startsWith('switch.');
    if (isSwitch) {
      return applyLightIntent(
        { ...zoned, on: s.state === 'on', brightness: null, available: true },
        intents?.[m.entity], s,
      );
    }
    const brightness = haBrightnessToPct(s.attributes?.brightness);
    return applyLightIntent({
      ...zoned,
      on: s.state === 'on',
      brightness: brightness != null
        ? brightness
        : (s.state === 'on' ? 100 : 0),
      available: true,
    }, intents?.[m.entity], s);
  });
}

// Build the utilities list from the mapping with live HA state.
// Utilities (exhaust fans, heated towel rails, etc.) are switched
// `light.*` or `switch.*` entities with no brightness concept — the
// row UI renders an on/off icon-button without a stepper. See
// [[project-fibaro-switched-loads-as-light]] for why Fibaro fans
// land under `light.*`.
function overlayUtilities(mapping, haStates, intents) {
  if (!mapping?.utilities || !haStates) return [];
  return mapping.utilities.map((u) => {
    const s = haStates[u.entity];
    if (!s) return { ...u, on: false, available: false };
    if (s.state === 'unavailable' || s.state === 'unknown') {
      return { ...u, on: false, available: false };
    }
    return applyLightIntent(
      { ...u, on: s.state === 'on', brightness: null, available: true },
      intents?.[u.entity], s,
    );
  });
}

// Average `current_position` across one or many cover entities.
// Returns the seed value if no entities are present in hass.states yet.
// Sun-tracking toggle overlay — 'on' | 'off' from the bound
// input_boolean; null when the room has no mapping or the boolean is
// unavailable, so the Shades "Automation" card only renders where the
// toggle actually exists and is live.
function overlaySunTracking(mapping, haStates) {
  if (!mapping.sunTracking || !haStates) return null;
  const st = haStates[mapping.sunTracking]?.state;
  return (st === 'on' || st === 'off') ? st : null;
}

function overlayCover(seed, ref, haStates) {
  if (!ref || !haStates) return seed;
  const ids = Array.isArray(ref) ? ref : [ref];
  const positions = ids
    .map((id) => haStates[id]?.attributes?.current_position)
    .filter((p) => p != null);
  if (positions.length === 0) return seed;
  return Math.round(positions.reduce((a, b) => a + b, 0) / positions.length);
}

// ── Cover travel detection ───────────────────────────────────────────
//
// HA's usual signal for "this cover is moving" is the entity's own
// `opening` / `closing` state. **These covers never report it.** The
// Fibaro integration leaves is_opening/is_closing unset, so the entity
// sits on `open` or `closed` for the entire throw. Verified two ways on
// 2026-07-19: 72 h of recorder history across three covers contains
// only open/closed/unavailable, and a real 30 s travel that morning
// logged state `open` throughout while current_position stepped
//     09:00:05 → 2, 09:00:15 → 35, 09:00:24 → 68, 09:00:35 → 100.
// `supported_features: 15` confirms open/close/set_position/stop with no
// travel reporting. So reading entity state would have been dead code.
//
// Position deltas are the real signal. Reports land roughly every 10 s,
// which sets COVER_SETTLE_MS: long enough not to flicker between
// reports, short enough that the tail after arrival stays brief. A move
// we commanded clears the moment it reaches its target, so that tail
// only ever applies to movement we didn't initiate — the ha-blinds
// sun-tracking watchdog repositioning on its own.
const COVER_SETTLE_MS = 14000;

// Cover groups a room exposes, as [key, entityRef] pairs.
const COVER_KINDS = ['blinds', 'sheer', 'louvre'];

function coverMotionKey(roomId, kind) { return `${roomId}:${kind}`; }

// Fold one observed position into a motion entry. Pure, and exported so
// the travel logic can be tested against a recorded real-world sequence
// without waiting on (or physically moving) a blind.
//
// A first sighting seeds with `at: 0` — a cover that merely exists at
// startup is at rest, not travelling.
export function nextCoverMotion(seen, pos, now) {
  if (!seen) return { pos, dir: null, at: 0 };
  if (seen.pos === pos) return seen;
  return { pos, dir: pos > seen.pos ? 'opening' : 'closing', at: now };
}

// Resolve a motion entry to 'opening' | 'closing' | null. `target` is
// the commanded destination when we issued the move, else null.
export function coverMovingFrom(seen, target, now) {
  if (!seen || !seen.at || !seen.dir) return null;
  if (now - seen.at > COVER_SETTLE_MS) return null;
  if (target != null && Math.abs(seen.pos - target) <= 2) return null;
  return seen.dir;
}

function overlaySonos(seedSonos, mapping, haStates) {
  if (!mapping?.sonos || !haStates) return seedSonos;
  const s = haStates[mapping.sonos];
  if (!s) return seedSonos;
  const att = s.attributes || {};
  // Show what's live; don't fall back to the seed's mock track string,
  // which makes idle speakers look like they're playing imaginary music.
  // `track` is null when nothing is queued — UI renders "Nothing playing".
  const hasMedia = !!att.media_title;
  return {
    ...seedSonos,
    playing: s.state === 'playing',
    track:  hasMedia
      ? (att.media_artist ? `${att.media_artist} — ${att.media_title}` : att.media_title)
      : null,
    artist: att.media_artist || null,
    // Raw fields used by the queue matcher in NowPlayingQueue —
    // `track` above is pre-formatted ("Artist — Title") for display
    // and doesn't equal any queue item's media_title. Exposing the
    // raw values lets the queue find the currently playing entry by
    // content_id (Spotify URI) or by exact title match.
    mediaTitle: att.media_title || null,
    mediaContentId: att.media_content_id || null,
    // `source` is what Sonos is currently sourcing from — 'Line-in'
    // when a line-in jack is active, an arbitrary station name for
    // some TuneIn streams, null/empty for ordinary Spotify playback.
    // Used by NowPlayingHero to render SourceCover instead of the
    // procedural CoverArt fallback when line-in is the source.
    source: att.source || null,
    volume: att.volume_level != null
      ? Math.round(att.volume_level * 100)
      : seedSonos.volume,
    muted:  !!att.is_volume_muted,
    // HA media_player exposes entity_picture as an authenticated URL
    // (it embeds a short-lived token). Same-origin, so an <img src> is
    // enough — no extra auth dance required. Falls back to procedural
    // CoverArt when absent.
    mediaImage: att.entity_picture || null,
    // Scrubber data: media_position is the position at
    // media_position_updated_at; UI ticks forward locally between HA
    // pushes. Both are seconds (number or null).
    mediaPosition: att.media_position != null ? att.media_position : null,
    mediaDuration: att.media_duration != null ? att.media_duration : null,
    mediaPositionUpdatedAt: att.media_position_updated_at || null,
    shuffle: !!att.shuffle,
    // HA reports 'off' | 'one' | 'all' for repeat. Default to 'off' when absent.
    repeat:  att.repeat || 'off',
    // Queue summary: zero-indexed position + total size. Full queue
    // contents need a media_player.browse_media WebSocket call —
    // deferred to slice 2b.
    queuePosition: att.queue_position != null ? att.queue_position : null,
    queueSize:     att.queue_size     != null ? att.queue_size     : null,
    // Sonos group membership: array of entity_ids that are grouped
    // together (includes the coordinator itself). Slice 2 will use
    // this to render the grouping pills.
    groupMembers: att.group_members || [],
  };
}

// `com.android.tv`, `com.netflix.ninja`, etc. are bundle IDs that the
// Apple TV / Sony BRAVIA integrations sometimes report when no
// human-readable app name is available. Strip those out — the UI can
// fall back to "Apple TV" / "Standby" instead.
function humanAppName(s) {
  if (!s) return null;
  if (/^[a-z][a-z0-9_-]*(\.[a-z0-9_-]+)+$/i.test(s)) return null;
  return s;
}

// Overlay TV state from media_player.<room>_tv. The BRAVIA / LG entities
// report 'on' / 'off' / 'playing' / 'paused' / 'idle' / 'standby' /
// 'unavailable'. We treat anything that isn't on/playing/paused as off
// (so 'unavailable' renders as Standby rather than crashing the tile).
function overlayTv(seedTv, mapping, haStates) {
  if (!seedTv || !mapping?.screen?.tv || !haStates) return seedTv;
  const s = haStates[mapping.screen.tv];
  if (!s) return seedTv;
  const att = s.attributes || {};
  const liveStates = new Set(['on', 'playing', 'paused', 'idle']);

  // The second, honest power read, where the room has one
  // (`screen.tvReal` — the braviatv entity in Office and Rumpus). Null
  // when no such entity is mapped, when HA has not seen it, or when it
  // has no opinion, so a braviatv outage falls back to the fast echo
  // rather than pinning the room asleep forever. Pavilion is webostv and
  // maps no tvReal: its `tv` entity is already a real read.
  const realS = mapping.screen.tvReal ? haStates[mapping.screen.tvReal] : null;
  const realAtt = realS?.attributes || {};
  const real = realS && realS.state !== 'unavailable' && realS.state !== 'unknown'
    ? liveStates.has(realS.state)
    : null;

  return {
    ...seedTv,
    on:     liveStates.has(s.state),
    // Power truth where it exists. `on` above stays as the fast echo —
    // it is what lights the tile the instant a press goes out — but
    // screenReads prefers this. See the tvReal note in MAPPINGS.
    real,
    // Source comes from whichever entity actually reports one, and the
    // androidtv_remote entity never does: verified against live state
    // 2026-09-27, `media_player.rumpus_tv`'s only attributes are
    // assumed_state / device_class / friendly_name / supported_features.
    // So in Office and Rumpus this resolved to `seedTv.source` on every
    // render and the Rumpus tile read "Screen · Apple TV" forever no
    // matter what input the TV was on. Deliberately no seed fallback
    // now: a fabricated source is worse than none, and screenLabel
    // already degrades to "Screen · Apple TV" on null.
    source: realAtt.source || realAtt.media_title || att.source || att.source_name || null,
    // Filter bundle-ID-shaped strings (e.g. com.android.tv) — the
    // BRAVIA integration sometimes reports those as `app_name` when no
    // friendly label is available. Don't carry the seed app forward;
    // when the TV is live, the seed string is misleading.
    app:    humanAppName(att.app_name) || humanAppName(att.app_id) || null,
    unavailable: s.state === 'unavailable' || s.state === 'unknown',
    // `assumed_state` means the integration writes the state it *asked*
    // for rather than one it read back. Office's TV is androidtv_remote,
    // which flips to `on` in the same event as the service call — so
    // this entity can confirm a command was sent, never that the screen
    // woke. Consumers that need settle-truth (the power affordance) must
    // read something else when this is set. See
    // [[assumed-state-cannot-settle-optimistic-ui]].
    assumed: att.assumed_state === true,
  };
}

// Apple TV is exposed as its own surface — independent from the TV's
// state, since the Apple TV reports app + now-playing even when the TV
// is asleep (it lives on standby and wakes on demand). Returns null if
// no Apple TV is mapped for this room.
function overlayAppleTv(mapping, haStates) {
  if (!mapping?.screen?.appleTv || !haStates) return null;
  const s = haStates[mapping.screen.appleTv];
  if (!s) return null;
  const att = s.attributes || {};
  return {
    state: s.state,                        // 'off' | 'on' | 'playing' | 'paused' | 'standby' | 'idle' | 'unavailable'
    app:   humanAppName(att.app_name) || humanAppName(att.app_id) || null,
    title: att.media_title || null,
    artist: att.media_artist || null,
    // Same shape as the Sonos overlay — Screen tile reuses Sonos's
    // useImageBlobUrl path + live-ticking position scrubber.
    mediaImage: att.entity_picture || null,
    mediaPosition: att.media_position != null ? att.media_position : null,
    mediaDuration: att.media_duration != null ? att.media_duration : null,
    mediaPositionUpdatedAt: att.media_position_updated_at || null,
  };
}

// Room AV idle (binary_sensor.<room>_av_idle): true when TV off + Apple
// TV off/standby + Sonos not playing. Drives ha-av's 30-min auto-off;
// also useful for "is this room awake?" indicators in the UI.
// Live scenes for rooms that map them (Office only, for now). Returns
// null when unmapped, so the room view falls back to the seed's mock
// names. `active` is the item whose id the state entity reads, or null
// when it reads anything else (Present, unknown, unavailable): no key is
// drawn chosen rather than guessing.
export function overlayScenes(mapping, haStates) {
  const m = mapping?.scenes;
  if (!m) return null;
  const st = haStates?.[m.state]?.state;
  return {
    items:  m.items.map(({ id, label }) => ({ id, label })),
    active: m.items.some((i) => i.id === st) ? st : null,
  };
}

function overlayIdle(mapping, haStates) {
  if (!mapping?.idle || !haStates) return null;
  const s = haStates[mapping.idle];
  if (!s) return null;
  return s.state === 'on';                 // binary_sensor: on == idle
}

// Apple-TV keyboard focus: pyatv exposes a binary_sensor that flips on
// when the foreground app is showing a text field (search box, password
// prompt, etc.). The Screen tile uses this for a subtle ⌨ hint; the
// media modal uses it to show an inline text input bar.
function overlayKeyboardFocus(mapping, haStates) {
  const ent = mapping?.screen?.appleTvKeyboardFocus;
  if (!ent || !haStates) return null;
  const s = haStates[ent];
  if (!s) return null;
  return s.state === 'on';
}

// Per-control audio overlay. Returns { centre, bass, treble, surround }
// where each control is { value, min, max, step, entity, domain } or
// undefined if not mapped for this room. Slug → domain split: anything
// that starts with `input_number.` uses the `input_number` service;
// everything else (`number.*`) uses the `number` service.
function overlayOneNumber(entity, haStates) {
  if (!entity || !haStates) return undefined;
  const s = haStates[entity];
  if (!s) return undefined;
  const att = s.attributes || {};
  const v = parseFloat(s.state);
  if (!isFinite(v)) return undefined;
  return {
    entity,
    domain: entity.split('.')[0],
    value: v,
    min:  att.min  != null ? Number(att.min)  : 0,
    max:  att.max  != null ? Number(att.max)  : 100,
    step: att.step != null ? Number(att.step) : 1,
    unit: att.unit_of_measurement || null,
  };
}
function overlayAudio(mapping, haStates) {
  const m = mapping?.audio;
  if (!m) return null;
  return {
    centre:   overlayOneNumber(m.centre,   haStates),
    surround: overlayOneNumber(m.surround, haStates),
  };
}

// EQ overlay — bass / treble resolved against whichever device actually
// owns tone in this room's chain, which is NOT always the device that
// owns volume (`primaryEntityFor` in music/shell.jsx). Pure-Sonos rooms
// own EQ on the Sonos; Pavilion and Rumpus own it on their Integra amp,
// because their Sonos is a line-level source feeding the amp rather than
// the amplifier itself. Kept apart from overlayAudio because centre and
// surround are amp-specific extras with no cross-room equivalent, while
// bass / treble are the one pair every owner has.
//
// Pool has no `eq:` mapping on purpose: its Connect feeds a Stewart
// SR2-125, which has no network control, so there is nothing to drive.
// Returns null rather than an empty shell so callers can gate cleanly.
function overlayEq(mapping, haStates) {
  const m = mapping?.eq;
  if (!m) return null;
  const bass   = overlayOneNumber(m.bass,   haStates);
  const treble = overlayOneNumber(m.treble, haStates);
  if (!bass && !treble) return null;
  return { owner: m.owner || null, bass, treble };
}

// Destinations overlay — derives the "Playing in" chip state for each
// destination this room exposes. Encodes the per-destination state
// machine: Kitchen ties to the DRX-3.1 main zone (hardware-linked to
// the TV main output, so locked while the TV is on); Dining ties to
// DRX-3.1 zone 2 or the extend_dining boolean; Pool extensions to
// each room read the matching pavilion/rumpus extend_pool boolean.
function overlayDestinations(roomId, mapping, haStates) {
  if (!mapping?.destinations) return null;
  const sOf = (e) => (e ? haStates?.[e] : null);
  return mapping.destinations.map((id) => {
    if (id === 'kitchen') {
      const amp = sOf(mapping.amp?.main);
      const tv  = sOf(mapping.screen?.tv);
      return {
        id, name: 'Kitchen',
        active: !!amp && amp.state !== 'off' && amp.state !== 'unavailable',
        locked: tv && tv.state !== 'off' && tv.state !== 'unavailable',
        lockReason: 'Kitchen is hardware-linked to the TV main zone',
      };
    }
    if (id === 'dining') {
      const z2 = sOf(mapping.amp?.zone2);
      const xt = sOf(mapping.pavilionMode?.extendDining);
      const zoneActive = !!z2 && z2.state !== 'off' && z2.state !== 'unavailable';
      const extendActive = xt?.state === 'on';
      return { id, name: 'Dining', active: zoneActive || extendActive };
    }
    if (id === 'pool') {
      const xp = sOf(mapping.av?.extendToPool);
      return { id, name: 'Pool', active: xp?.state === 'on' };
    }
    if (id === 'rumpus') {
      const amp = sOf(mapping.amp?.main);
      const tv  = sOf(mapping.screen?.tv);
      return {
        id, name: 'Rumpus',
        active: !!amp && amp.state !== 'off' && amp.state !== 'unavailable',
        locked: tv && tv.state !== 'off' && tv.state !== 'unavailable',
        lockReason: 'Rumpus is hardware-linked to the TV main zone',
      };
    }
    return null;
  }).filter(Boolean);
}

// Louvres overlay — Fibaro pulse-pair pattern, optionally wrapped by a
// `cover.*` entity for state tracking (Pool louvre is wrapped; High,
// West, Rumpus are not). Returns an array carrying the mapping's
// open/close switches + the live state when available. Components
// track local "last-tapped" state for louvres without a cover entity.
function overlayLouvres(mapping, haStates) {
  if (!mapping?.louvres) return null;
  return mapping.louvres.map((l) => {
    // Preferred: read state from the dedicated input_boolean
    // (`stateBoolean`) — it reflects last user intent and is what the
    // panel writes to. Fallback to the cover.* entity for louvres
    // that have one (Pool); fallback further to 'unknown'.
    const ib = l.stateBoolean ? haStates?.[l.stateBoolean] : null;
    if (ib?.state === 'on')  return { ...l, state: 'open' };
    if (ib?.state === 'off') return { ...l, state: 'closed' };
    const cov = l.coverEntity ? haStates?.[l.coverEntity] : null;
    const raw = cov?.state;
    const state =
      raw === 'open'   || raw === 'opening' ? 'open'
      : raw === 'closed' || raw === 'closing' ? 'closed'
      : 'unknown';
    return { ...l, state };
  });
}

// Climate overlay. Reads current_temperature / target_temp / hvac_mode
// / current_humidity from the bound climate entity. Returns null if
// no mapping; caller upstream strips the seed climate fields so the
// climate tile + temp subtitle don't show. When the same climate
// entity is bound to multiple rooms (Heritage HVAC = Office + Master
// Bedroom), each room reads the same live state — adjustments from
// either room's tile drive the shared unit.
// Climate has the same clobber problem lights had: `update()` writes the
// new target into mockRooms, then overlayClimate overwrites it with HA's
// not-yet-updated value, so a press on ± visibly bounced back. Same fix,
// keyed by the climate entity.
//
// Target tolerance is 0.05° — HA round-trips setpoints as floats, so an
// exact equality check would never settle. Mode is compared against the
// entity state, which mirrors hvac_mode.
export function climateIntentSettled(intent, s) {
  if (!s) return false;
  if (intent.mode != null && s.state !== intent.mode) return false;
  if (intent.target != null) {
    const actual = s.attributes?.temperature;
    if (actual == null || Math.abs(Number(actual) - intent.target) > 0.05) return false;
  }
  return true;
}

function applyClimateIntent(live, intent, s) {
  if (!live || !intent || climateIntentSettled(intent, s)) return live;
  return {
    ...live,
    target: intent.target != null ? intent.target : live.target,
    mode:   intent.mode   != null ? intent.mode   : live.mode,
    pending: true,
  };
}

function overlayClimate(mapping, haStates) {
  const ent = mapping?.climate;
  if (!ent || !haStates) return null;
  const s = haStates[ent];
  if (!s) return null;
  const a = s.attributes || {};
  return {
    entity: ent,
    state:  s.state,
    temp:   a.current_temperature != null ? Number(a.current_temperature) : null,
    target: a.temperature         != null ? Number(a.temperature)         : null,
    mode:   s.state,                       // hvac_mode is mirrored in entity state
    // hvac_action reports what the unit is *actually doing* (heating /
    // cooling / idle / off / drying / fan) — distinct from mode, which
    // is just what's set. Not all integrations expose it (Heritage HVAC
    // doesn't as of 2026-05-27); callers should fall back to mode when
    // null.
    action: a.hvac_action || null,
    humidity: a.current_humidity != null ? Number(a.current_humidity) : null,
  };
}

// Pool extension is a household-level state, not per-room. Both
// `input_boolean.pavilion_extend_pool` and `..._rumpus_extend_pool`
// route through media_player.drx_5_zone_3, so only one can be on at a
// time (the UI must enforce — see HANDOFF.md §4 #13). Exposed as a
// single object so the AMediaModal toggle can render mutual-exclusion
// context ("currently extended from Rumpus — toggling switches the
// source").
function overlayPoolExtension(haStates) {
  if (!haStates) return { pavilionActive: false, rumpusActive: false };
  return {
    pavilionActive: haStates['input_boolean.pavilion_extend_pool']?.state === 'on',
    rumpusActive:   haStates['input_boolean.rumpus_extend_pool']?.state === 'on',
  };
}

// Live state for each speaker in SONOS_SPEAKERS. Used by the Sonos
// modal to drive the grouping pills + per-member volume sliders.
// Returns the registry shape augmented with state/volume/muted/
// groupMembers from haStates. Missing entities (offline, integration
// reloading) render as muted/zero-volume placeholders rather than
// disappearing — keeps the grouping UI stable.
function overlaySpeakers(haStates) {
  return SONOS_SPEAKERS.map((sp) => {
    const e = haStates?.[sp.entity];
    const a = e?.attributes || {};
    return {
      ...sp,
      state:   e?.state || 'unknown',
      volume:  a.volume_level != null ? Math.round(a.volume_level * 100) : 0,
      muted:   !!a.is_volume_muted,
      groupMembers: a.group_members || [],
      available: !!e && e.state !== 'unavailable' && e.state !== 'unknown',
    };
  });
}

// Shared machinery behind both intent maps. Retires an intent as soon as
// HA agrees with it, and sweeps anything that outlived INTENT_TTL — the
// silent case (command dropped, device offline) has no state push to
// trigger the first effect, so it needs its own timer.
//
// `settled` must be a stable reference; pass a module-level function.
function useIntentMap(haStates, settled) {
  const [intents, setIntents] = useState({});

  useEffect(() => {
    setIntents((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;          // identity keeps no-op passes from looping
      const now = Date.now();
      const next = {};
      let changed = false;
      for (const id of keys) {
        const intent = prev[id];
        if (now - intent.at > INTENT_TTL || settled(intent, haStates?.[id])) changed = true;
        else next[id] = intent;
      }
      return changed ? next : prev;
    });
  }, [haStates, settled]);

  useEffect(() => {
    if (Object.keys(intents).length === 0) return;
    const id = setTimeout(() => {
      setIntents((prev) => {
        const now = Date.now();
        const next = Object.fromEntries(
          Object.entries(prev).filter(([, i]) => now - i.at <= INTENT_TTL)
        );
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, INTENT_TTL + 250);
    return () => clearTimeout(id);
  }, [intents]);

  // Merges onto any existing intent for the entity, so changing mode
  // doesn't discard an in-flight setpoint (or vice versa), and restamps
  // `at` — both the TTL sweep and the settle check work from it.
  const note = useCallback((entity, patch) => {
    setIntents((prev) => ({
      ...prev,
      [entity]: { ...prev[entity], ...patch, at: Date.now() },
    }));
  }, []);

  return [intents, note];
}

export function usePanelRooms(hass, initialId = 'office') {
  // Mock state is the source of truth for un-mapped fields and for rooms
  // without any mapping. HA overlay replaces specific fields where wired.
  const [mockRooms, setMockRooms] = useState(() =>
    JSON.parse(JSON.stringify(ROOMS))
  );
  const [activeId, setActiveId] = useState(initialId);

  // Pending intents, keyed by entity_id. See the intent layer above.
  // Two maps rather than one because the settle tests differ: lights
  // compare on/brightness, climate compares setpoint and hvac mode.
  const [lightIntents, noteLightIntent] = useIntentMap(hass?.states, lightIntentSettled);
  const [climateIntents, noteClimateIntent] = useIntentMap(hass?.states, climateIntentSettled);

  // Last observed position per cover group, with when it last changed —
  // the basis for travel detection. See COVER_SETTLE_MS.
  const [coverMotion, setCoverMotion] = useState({});
  // Commanded destination per cover group, so a move we started can
  // clear the instant it arrives instead of waiting out the settle
  // window. Cleared on stop (destination unknowable) and on arrival.
  const coverTargets = useRef({});

  const haStates = hass?.states;

  // Watch every mapped cover group's averaged position and stamp the
  // moment it changes. The first sighting seeds with `at: 0` so a cover
  // that merely exists at startup isn't reported as travelling.
  useEffect(() => {
    if (!haStates) return;
    setCoverMotion((prev) => {
      let next = prev;
      for (const [roomId, mapping] of Object.entries(MAPPINGS)) {
        for (const kind of COVER_KINDS) {
          if (!mapping[kind]) continue;
          const pos = overlayCover(null, mapping[kind], haStates);
          if (pos == null) continue;
          const key = coverMotionKey(roomId, kind);
          const seen = prev[key];
          const stepped = nextCoverMotion(seen, pos, Date.now());
          if (stepped !== seen) next = { ...next, [key]: stepped };
        }
      }
      return next;
    });
  }, [haStates]);

  // Nothing pushes state when a settle window merely elapses, so wake
  // the tree once the freshest motion stamp ages out.
  useEffect(() => {
    const freshest = Object.values(coverMotion)
      .reduce((max, m) => Math.max(max, m.at || 0), 0);
    if (!freshest) return;
    const remaining = COVER_SETTLE_MS - (Date.now() - freshest);
    if (remaining <= 0) return;
    const id = setTimeout(() => setCoverMotion((p) => ({ ...p })), remaining + 100);
    return () => clearTimeout(id);
  }, [coverMotion]);

  // 'opening' | 'closing' | null for one cover group.
  const coverMovingFor = useCallback((roomId, kind) => {
    const key = coverMotionKey(roomId, kind);
    return coverMovingFrom(coverMotion[key], coverTargets.current[key] ?? null, Date.now());
  }, [coverMotion]);

  const rooms = useMemo(() => {
    if (!haStates) return mockRooms;
    return mockRooms.map((r) => {
      const mapping = MAPPINGS[r.id];
      if (!mapping) return r;

      // Climate: when mapped, overlay live temp/target/mode from the
      // bound climate entity. When unmapped, strip the mock seed
      // climate fields so the tile + subtitle don't show false data.
      const base = { ...r };
      const climateLive = applyClimateIntent(
        overlayClimate(mapping, haStates),
        climateIntents[mapping.climate],
        haStates[mapping.climate],
      );
      if (climateLive) {
        if (climateLive.temp   != null) base.temp   = climateLive.temp;
        if (climateLive.target != null) base.target = climateLive.target;
        if (climateLive.mode   != null) base.mode   = climateLive.mode;
        if (climateLive.humidity != null) base.humidity = climateLive.humidity;
        // A room sensor, where mapped, outranks the unit's own reading:
        // Heritage's current_temperature is return air in the hall, which
        // ran 3-4° above the bedroom on 2026-10-28. Falls back to the unit
        // while the sensor is unavailable rather than showing nothing.
        const t = Number(haStates[mapping.tempSensor]?.state);
        if (mapping.tempSensor && Number.isFinite(t)) base.temp = t;
      } else if (mapping.climate == null) {
        delete base.temp;
        delete base.target;
        delete base.mode;
        delete base.humidity;
        // No setpoint, so the room page shows no climate controls; the
        // reading surfaces in the drawer, home tile and inside average.
        const t = Number(haStates[mapping.tempSensor]?.state);
        if (mapping.tempSensor && Number.isFinite(t)) base.temp = t;
        const h = Number(haStates[mapping.humiditySensor]?.state);
        if (mapping.humiditySensor && Number.isFinite(h)) base.humidity = h;
      }

      return {
        ...base,
        lights:    overlayLights(r.lights, mapping, haStates, lightIntents),
        utilities: overlayUtilities(mapping, haStates, lightIntents),
        blinds:  overlayCover(r.blinds, mapping.blinds, haStates),
        sunTracking: overlaySunTracking(mapping, haStates),
        louvre:  overlayCover(r.louvre, mapping.louvre, haStates),  // legacy single-cover slider; rooms with `louvres:` use the array below
        louvres: overlayLouvres(mapping, haStates),
        destinations: overlayDestinations(r.id, mapping, haStates),
        sheer:   overlayCover(r.sheer,  mapping.sheer,  haStates),
        // Live travel per cover group, inferred from position deltas —
        // see the cover-travel note above for why entity state can't be
        // used here.
        blindsMoving: coverMovingFor(r.id, 'blinds'),
        sheerMoving:  coverMovingFor(r.id, 'sheer'),
        louvreMoving: coverMovingFor(r.id, 'louvre'),
        sonos:   overlaySonos(r.sonos, mapping, haStates),
        tv:      overlayTv(r.tv, mapping, haStates),
        appleTv: overlayAppleTv(mapping, haStates),
        keyboardFocus: overlayKeyboardFocus(mapping, haStates),
        audio:   overlayAudio(mapping, haStates),
        eq:      overlayEq(mapping, haStates),
        liveScenes: overlayScenes(mapping, haStates),
        idle:    overlayIdle(mapping, haStates),
        climate: climateLive,
        // av is the *static* MAPPINGS shape (which scripts/toggles exist
        // for this room) — components read it to decide which verbs to
        // render. Live state for those verbs comes via room.idle and the
        // various media_player overlays above; the verbs themselves are
        // fire-and-forget, dispatched through act().
        av:      mapping.av || null,
      };
    });
  }, [mockRooms, haStates, lightIntents, climateIntents, coverMovingFor]);

  const active = rooms.find((r) => r.id === activeId) || rooms[0];

  const update = useCallback((id, patch) => {
    // Local mock update — keeps un-mapped rooms responsive and serves as
    // optimistic UI for mapped rooms before HA state catches up.
    setMockRooms((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );

    const mapping = MAPPINGS[id];
    if (!mapping || !hass) return;

    // Each set_cover_position also records its destination, so travel
    // detection can clear on arrival rather than on the settle timer.
    if (patch.blinds != null && mapping.blinds) {
      coverTargets.current[coverMotionKey(id, 'blinds')] = patch.blinds;
      hass.callService('cover', 'set_cover_position', {
        entity_id: mapping.blinds,    // string OR string[] — HA accepts both
        position: patch.blinds,
      });
    }
    if (patch.louvre != null && mapping.louvre) {
      coverTargets.current[coverMotionKey(id, 'louvre')] = patch.louvre;
      hass.callService('cover', 'set_cover_position', {
        entity_id: mapping.louvre,
        position: patch.louvre,
      });
    }
    if (patch.sheer != null && mapping.sheer) {
      coverTargets.current[coverMotionKey(id, 'sheer')] = patch.sheer;
      hass.callService('cover', 'set_cover_position', {
        entity_id: mapping.sheer,
        position: patch.sheer,
      });
    }
    // Sun-tracking day toggle — writes the ha-blinds model selector.
    // The blinds physically converge via the watchdog (~1 min), not
    // instantly; the optimistic mock update above keeps the pill
    // responsive meanwhile.
    if (patch.sunTracking != null && mapping.sunTracking) {
      hass.callService(
        'input_boolean',
        patch.sunTracking === 'on' ? 'turn_on' : 'turn_off',
        { entity_id: mapping.sunTracking }
      );
    }
    if (patch.sonos && mapping.sonos) {
      // Volume changes go through media_player.volume_set; play/pause via
      // media_player.media_play / media_pause. Track changes from the
      // UI's mock search are not pushed to HA yet (M2 work).
      if (patch.sonos.volume != null) {
        hass.callService('media_player', 'volume_set', {
          entity_id: mapping.sonos,
          volume_level: patch.sonos.volume / 100,
        });
      }
      if (patch.sonos.playing != null) {
        hass.callService(
          'media_player',
          patch.sonos.playing ? 'media_play' : 'media_pause',
          { entity_id: mapping.sonos }
        );
      }
    }
    // Climate write paths — target temperature → climate.set_temperature,
    // mode → climate.set_hvac_mode. Both routes through the room's bound
    // climate entity (e.g. climate.heritage for Office + Master Bed).
    if (patch.target != null && mapping.climate) {
      noteClimateIntent(mapping.climate, { target: patch.target });
      hass.callService('climate', 'set_temperature', {
        entity_id: mapping.climate,
        temperature: patch.target,
      });
    }
    if (patch.mode != null && mapping.climate) {
      noteClimateIntent(mapping.climate, { mode: patch.mode });
      hass.callService('climate', 'set_hvac_mode', {
        entity_id: mapping.climate,
        hvac_mode: patch.mode,
      });
    }
    // tv: TBD — not exercised by the panel today.
  }, [hass, noteClimateIntent]);

  const updateLight = useCallback((roomId, lightId, patch) => {
    setMockRooms((prev) =>
      prev.map((r) => {
        if (r.id !== roomId) return r;
        return {
          ...r,
          lights: r.lights.map((l) =>
            l.id === lightId ? { ...l, ...patch } : l
          ),
        };
      })
    );

    const lightMap = MAPPINGS[roomId]?.lights?.find((x) => x.id === lightId);
    if (!lightMap || !hass) return;

    // Record the intent so the overlay shows what was asked for rather
    // than snapping back to HA's not-yet-updated truth. `on` is derived
    // the same way the service call below decides turn_on vs turn_off,
    // so the two can't disagree.
    noteLightIntent(lightMap.entity, {
      on: patch.on != null ? patch.on : (patch.brightness ?? 0) > 0,
      brightness: patch.brightness,
    });

    // Route to light.* services for proper light entities, switch.*
    // services for smart-plug-backed "lights" (master bedside lamps,
    // etc.). For switches, brightness is meaningless — ignore the arg.
    const domain = lightMap.entity.startsWith('switch.') ? 'switch' : 'light';

    if (patch.on === false) {
      hass.callService(domain, 'turn_off', { entity_id: lightMap.entity });
    } else if (patch.on === true || patch.brightness != null) {
      const args = { entity_id: lightMap.entity };
      if (domain === 'light' && patch.brightness != null) {
        args.brightness = pctToHaBrightness(patch.brightness);
      }
      hass.callService(domain, 'turn_on', args);
    }
  }, [hass, noteLightIntent]);

  // Utility on/off — exhaust fans, heated towel rails, etc. Routed
  // separately from updateLight because utilities live in their own
  // MAPPINGS array (so the lights grid stays "fixture lights only")
  // and never carry brightness. Domain follows the entity prefix.
  const updateUtility = useCallback((roomId, utilityId, patch) => {
    setMockRooms((prev) =>
      prev.map((r) => {
        if (r.id !== roomId) return r;
        return {
          ...r,
          utilities: (r.utilities || []).map((u) =>
            u.id === utilityId ? { ...u, ...patch } : u
          ),
        };
      })
    );

    const utilMap = MAPPINGS[roomId]?.utilities?.find((x) => x.id === utilityId);
    if (!utilMap || !hass) return;
    noteLightIntent(utilMap.entity, { on: !!patch.on });
    const domain = utilMap.entity.startsWith('switch.') ? 'switch' : 'light';
    hass.callService(domain, patch.on ? 'turn_on' : 'turn_off', {
      entity_id: utilMap.entity,
    });
  }, [hass, noteLightIntent]);

  // Fire a coordinated AV verb (watch / listen / listenDining / off /
  // extendToPool / extendDining). Resolves against MAPPINGS[].av — a
  // value starting `script.*` calls the script service; `input_boolean.*`
  // toggles the boolean (bi-directional sync in ha-av drives the
  // underlying extend/unextend scripts).
  //
  // UX caller's responsibility: enforce the pavilion/rumpus extendToPool
  // mutex — both ride drx_5_zone_3 and can't be active simultaneously.
  // See MAPPINGS top-of-file comment + HANDOFF-ha-av.md §4 #13.
  const act = useCallback((roomId, verb) => {
    if (!hass) return;
    const av = MAPPINGS[roomId]?.av;
    if (!av) return;
    const target = av[verb];
    if (!target) return;

    if (target.startsWith('script.')) {
      hass.callService('script', target.slice('script.'.length));
      return;
    }
    if (target.startsWith('input_boolean.')) {
      hass.callService('input_boolean', 'toggle', { entity_id: target });
      return;
    }
    // Unknown shape — log so we notice rather than fail silently.
    console.warn('[ha-controller] act(): unhandled target', { roomId, verb, target });
  }, [hass]);

  // Run a room scene. Resolves against MAPPINGS[].scenes.items; every
  // live scene is a script, so a re-press of the current one re-applies.
  const runScene = useCallback((roomId, sceneId) => {
    if (!hass) return;
    const item = MAPPINGS[roomId]?.scenes?.items.find((i) => i.id === sceneId);
    if (!item) return;
    hass.callService('script', item.script.slice('script.'.length));
  }, [hass]);

  // Fire an Apple-TV remote command. Resolves to `remote.send_command`
  // on `MAPPINGS[roomId].screen.appleTvRemote`.
  //
  // Command names are not a fixed list. HA's apple_tv remote looks the
  // name up in its own COMMAND_TO_ATTRIBUTE map, then falls back to
  // `getattr(atv.remote_control, name)`, so ANY pyatv RemoteControl
  // method is callable: the familiar 'home', 'menu', 'select', 'up',
  // 'down', 'left', 'right', 'play_pause', 'wakeup', 'siri', and also
  // 'skip_forward' / 'skip_backward'. An unknown name raises
  // ValueError in HA, which surfaces as a failed service call, not as
  // a silent no-op.
  //
  // opts (all optional, all pass-through to remote.send_command):
  //   hold_secs   — >= 1 makes HA call the pyatv method with
  //                 InputAction.Hold, i.e. a real long-press. This is
  //                 SEMANTIC, not wall-clock: HA does not hold the
  //                 button for that long, it sends one held press and
  //                 returns. Any "holding..." affordance in the UI is
  //                 therefore ours to time, not a reflection of the
  //                 TV. Verified in HA core apple_tv/remote.py,
  //                 2026-11-01.
  //   num_repeats — send the command N times. Used by the touchpad
  //                 scrub to turn swipe distance into a bigger jump in
  //                 apps that publish no duration and so cannot have a
  //                 real scrub bar.
  //   delay_secs  — gap between repeats.
  //
  // EXCEPT skip_forward / skip_backward, which go to
  // `apple_tv_audio.press` (ha-av) as a Companion Right / Left with the
  // same repeats and delay. send_command routes skips over MRP, and
  // Disney+ live refuses MRP skips while HA reports success; a Companion
  // arrow skipped 10 s in Disney+ live and on-demand, Kayo and Netflix
  // (measured 2026-11-01). One press is exact; 30 land within about 20%.
  const sendRemoteCommand = useCallback((roomId, command, opts) => {
    if (!hass) return;
    const screen = MAPPINGS[roomId]?.screen;
    const remote = screen?.appleTvRemote;
    if (!remote) return;
    if ((command === 'skip_forward' || command === 'skip_backward') && screen.appleTv) {
      const data = {
        entity_id: screen.appleTv,
        button: command === 'skip_forward' ? 'right' : 'left',
      };
      if (opts?.numRepeats != null) data.repeats = opts.numRepeats;
      if (opts?.delaySecs != null) data.delay = opts.delaySecs;
      Promise.resolve(hass.callService('apple_tv_audio', 'press', data)).catch((e) => {
        console.warn('[ha-controller] sendRemoteCommand(): press refused', { roomId, command }, e?.message || e);
      });
      return;
    }
    const data = { entity_id: remote, command };
    if (opts?.holdSecs != null) data.hold_secs = opts.holdSecs;
    if (opts?.numRepeats != null) data.num_repeats = opts.numRepeats;
    if (opts?.delaySecs != null) data.delay_secs = opts.delaySecs;
    hass.callService('remote', 'send_command', data);
  }, [hass]);

  // Browse the Sonos media tree. Call with no contentId/contentType to
  // get the root; drill in by passing a child's media_content_id/_type.
  // Returns the node response: { media_content_id, title, can_play,
  // can_expand, thumbnail, children: [...] } — each child has the same
  // shape and is independently playable (when can_play is true).
  const browseSonos = useCallback(async (roomId, contentId, contentType) => {
    if (!hass) return null;
    const entity_id = MAPPINGS[roomId]?.sonos;
    if (!entity_id) return null;
    try {
      const msg = { type: 'media_player/browse_media', entity_id };
      if (contentId !== undefined && contentId !== null) msg.media_content_id = contentId;
      if (contentType !== undefined && contentType !== null) msg.media_content_type = contentType;
      return await hass.callWS(msg);
    } catch (e) {
      console.error('[ha-controller] browseSonos failed', e);
      return null;
    }
  }, [hass]);

  // Play arbitrary media on the room's Sonos. Used for favourites,
  // playlists, library tracks — anywhere we have a (content_id,
  // content_type) pair from browseSonos. Errors are logged (not
  // toasted) — Sonos rejects some content types depending on the
  // current group state / source, and we want the console signal
  // when debugging "favourite didn't play".
  const playMedia = useCallback(async (roomId, contentId, contentType) => {
    if (!hass) return;
    const entity_id = MAPPINGS[roomId]?.sonos;
    if (!entity_id || !contentId) return;
    try {
      await hass.callService('media_player', 'play_media', {
        entity_id,
        media_content_id: contentId,
        media_content_type: contentType || 'music',
      });
    } catch (e) {
      console.error('[ha-controller] play_media failed', { entity_id, contentId, contentType, error: e?.message || e });
    }
  }, [hass]);

  // Fetch the current Sonos queue. Returns an array of items each with
  // { media_title, media_artist, media_album_name, media_content_id }.
  // Uses sonos.get_queue (response service). Empty array on failure.
  const getSonosQueue = useCallback(async (roomId) => {
    if (!hass) return [];
    const entity_id = MAPPINGS[roomId]?.sonos;
    if (!entity_id) return [];
    try {
      const result = await hass.callService(
        'sonos', 'get_queue',
        { entity_id },
        undefined, // target
        undefined, // notifyOnError
        true,      // returnResponse
      );
      // Response shape: { service_response: { <entity_id>: [...] } }
      // or callService might already unwrap. Defensive on both.
      const resp = result?.response || result?.service_response || result;
      return resp?.[entity_id] || [];
    } catch (e) {
      console.error('[ha-controller] getSonosQueue failed', e);
      return [];
    }
  }, [hass]);

  // Generic live state reader for any media_player entity_id. Used by
  // the AMediaModal to read amp volume (Pavilion DRX-3.1 / Rumpus
  // DRX-5) and the drx_5_zone_3 Pool-extension volume, both of which
  // are outside the per-room sonos overlay. Returns null if the
  // entity isn't in hass.states yet.
  const mediaPlayer = useCallback((entity_id) => {
    if (!entity_id || !hass?.states) return null;
    const e = hass.states[entity_id];
    if (!e) return null;
    const a = e.attributes || {};
    return {
      entity: entity_id,
      state:  e.state,
      volume: a.volume_level != null ? Math.round(a.volume_level * 100) : 0,
      muted:  !!a.is_volume_muted,
      friendlyName: a.friendly_name || entity_id,
    };
  }, [hass]);

  // Open / Close / Stop a percentage cover (blinds, sheers). The Open
  // and Close services trigger a full motion without specifying a
  // position — the cover runs to its endpoint. Stop interrupts an
  // in-motion cover, fixing the long-standing slider-drag bug where
  // continuous set_cover_position calls confused the motor.
  const coverCommand = useCallback((roomId, which, action) => {
    if (!hass) return;
    const entity = MAPPINGS[roomId]?.[which];
    if (!entity) return;
    const svc =
      action === 'open'  ? 'open_cover'  :
      action === 'close' ? 'close_cover' :
      action === 'stop'  ? 'stop_cover'  : null;
    if (!svc) return;
    // Remember where this is headed so travel detection can stop the
    // moment it lands. Stop has no knowable destination, so drop any
    // stale target and let the settle window handle it.
    const key = coverMotionKey(roomId, which);
    if (action === 'open')       coverTargets.current[key] = 100;
    else if (action === 'close') coverTargets.current[key] = 0;
    else                         delete coverTargets.current[key];
    hass.callService('cover', svc, { entity_id: entity });
  }, [hass]);

  // Drive a motorised louvre: pulse the Fibaro switch AND update the
  // companion `input_boolean.<…>_open` so the panel's Open/Closed
  // selection reflects last user intent across reloads + devices.
  // Pass `{ switch, stateBoolean, target }` where target is 'open' or
  // 'closed'. If stateBoolean is missing (e.g. legacy mapping without
  // it), only the switch fires — same as before.
  const louvreCommand = useCallback(({ switch: switchEntityId, stateBoolean, target }) => {
    if (!hass) return;
    if (switchEntityId) {
      hass.callService('switch', 'turn_on', { entity_id: switchEntityId });
    }
    if (stateBoolean && (target === 'open' || target === 'closed')) {
      hass.callService('input_boolean', target === 'open' ? 'turn_on' : 'turn_off', { entity_id: stateBoolean });
    }
  }, [hass]);

  // Destinations chip dispatch — orchestrates the per-chip state
  // machine. Each room defines its destination ids in MAPPINGS;
  // overlayDestinations computes the live `active`/`locked` flags;
  // this handler turns chip toggles into the right script + service
  // calls so the underlying state stays consistent.
  //   • Kitchen on/off  → script.pavilion_listen / script.pavilion_off
  //   • Dining on/off   → contextual on Kitchen state: extend_dining
  //     boolean when Kitchen is on, listen_dining script when it isn't
  //   • Pool on/off     → routes through togglePoolExtension (defined
  //     below; handles mutual exclusion with the other room)
  //   • Rumpus on/off   → script.rumpus_listen / script.rumpus_off
  // Locked chips are no-ops at the action level — the UI also greys
  // them, but the safety check is here too in case something fires
  // them programmatically.
  const destinationsCommand = useCallback((roomId, destId, target) => {
    if (!hass) return;
    const mapping = MAPPINGS[roomId];
    if (!mapping) return;
    const on = !!target;

    if (destId === 'kitchen' && roomId === 'pavilion') {
      // Locked if TV is on — caller should guard, but no-op here too.
      const tv = hass.states?.[mapping.screen?.tv];
      const tvOn = tv && tv.state !== 'off' && tv.state !== 'unavailable';
      if (!on && tvOn) return;
      hass.callService('script', on ? 'pavilion_listen' : 'pavilion_off');
      return;
    }
    if (destId === 'dining' && roomId === 'pavilion') {
      const amp = hass.states?.[mapping.amp?.main];
      const kitchenOn = amp && amp.state !== 'off' && amp.state !== 'unavailable';
      if (on) {
        if (kitchenOn) {
          hass.callService('input_boolean', 'turn_on', { entity_id: 'input_boolean.pavilion_extend_dining' });
        } else {
          hass.callService('script', 'pavilion_listen_dining');
        }
      } else {
        if (kitchenOn) {
          hass.callService('input_boolean', 'turn_off', { entity_id: 'input_boolean.pavilion_extend_dining' });
        } else {
          // Dining-only mode → turning Dining off means everything off.
          hass.callService('script', 'pavilion_off');
        }
      }
      return;
    }
    if (destId === 'pool') {
      // Pool extension reuses the existing togglePoolExtension defined
      // below — it handles the mutual exclusion with the other room's
      // extension (they share drx_5_zone_3).
      togglePoolExtensionImpl(roomId, on);
      return;
    }
    if (destId === 'rumpus' && roomId === 'rumpus') {
      const tv = hass.states?.[mapping.screen?.tv];
      const tvOn = tv && tv.state !== 'off' && tv.state !== 'unavailable';
      if (!on && tvOn) return;
      hass.callService('script', on ? 'rumpus_listen' : 'rumpus_off');
      return;
    }
  }, [hass]);

  // Inline implementation of togglePoolExtension so destinationsCommand
  // can call it without circular useCallback dependencies. The named
  // togglePoolExtension below is the same function exported to the UI.
  const togglePoolExtensionImpl = (roomId, on) => {
    if (!hass) return;
    const ours = MAPPINGS[roomId]?.av?.extendToPool;
    if (!ours) return;
    const others = ['pavilion', 'rumpus']
      .filter((r) => r !== roomId)
      .map((r) => MAPPINGS[r]?.av?.extendToPool)
      .filter(Boolean);
    const otherActive = others.find((ent) => hass.states?.[ent]?.state === 'on');
    if (on && otherActive) {
      hass.callService('input_boolean', 'turn_off', { entity_id: otherActive });
      setTimeout(() => {
        hass.callService('input_boolean', 'turn_on', { entity_id: ours });
      }, 600);
    } else {
      hass.callService('input_boolean', on ? 'turn_on' : 'turn_off', { entity_id: ours });
    }
  };

  // Pool-extension toggle with mutual-exclusion enforcement. Both
  // input_boolean.pavilion_extend_pool and ..._rumpus_extend_pool ride
  // media_player.drx_5_zone_3 — only one can be on at a time
  // (HANDOFF.md §4 #13). When the user enables one while the other is
  // active, first fire turn_off on the active one with a 600 ms gap
  // before turning on the new one. ha-av's extend/unextend scripts
  // handle the underlying DRX-5 reconfig in both directions.
  const togglePoolExtension = useCallback((roomId, on) => {
    if (!hass) return;
    const ours = MAPPINGS[roomId]?.av?.extendToPool;
    if (!ours) return;
    const others = ['pavilion', 'rumpus']
      .filter((r) => r !== roomId)
      .map((r) => MAPPINGS[r]?.av?.extendToPool)
      .filter(Boolean);
    const otherActive = others.find((ent) => hass.states?.[ent]?.state === 'on');

    if (on && otherActive) {
      hass.callService('input_boolean', 'turn_off', { entity_id: otherActive });
      setTimeout(() => {
        hass.callService('input_boolean', 'turn_on', { entity_id: ours });
      }, 600);
    } else {
      hass.callService('input_boolean', on ? 'turn_on' : 'turn_off', { entity_id: ours });
    }
  }, [hass]);

  // Per-speaker commands — operate on an arbitrary Sonos entity_id
  // (not gated by MAPPINGS), used by the Sonos modal's grouping +
  // per-member volume UI. `unjoin` here targets the *member*, not the
  // coordinator; sonosCommand below still handles the coordinator-
  // level 'join' that adds members to the current room's group.
  const sonosSpeakerCommand = useCallback((entity_id, action, value) => {
    if (!hass || !entity_id) return;
    const call = (svc, extra = {}) =>
      hass.callService('media_player', svc, { entity_id, ...extra });
    switch (action) {
      case 'volume': call('volume_set',  { volume_level: capVolume(entity_id, value) / 100 }); break;
      case 'mute':   call('volume_mute', { is_volume_muted: !!value }); break;
      case 'unjoin': call('unjoin'); break;
      case 'play':       call('media_play'); break;
      case 'pause':      call('media_pause'); break;
      case 'play_pause': call('media_play_pause'); break;
      default:
        console.warn('[ha-controller] sonosSpeakerCommand(): unknown action', { entity_id, action, value });
    }
  }, [hass]);

  // Sonos transport + grouping commands. Action is one of:
  //   'play_pause' | 'play' | 'pause' | 'next' | 'prev'
  //   'shuffle' (value: bool) | 'repeat' (value: 'off'|'one'|'all')
  //   'seek' (value: seconds) | 'mute' (value: bool)
  //   'join' (value: array of entity_ids to join as a group)
  //   'unjoin'
  // Volume is intentionally kept on update() since it's already part
  // of the existing patch flow — the room.sonos.volume path is a
  // user-driven slider, not an AV "verb".
  const sonosCommand = useCallback((roomId, action, value) => {
    if (!hass) return;
    const sonos = MAPPINGS[roomId]?.sonos;
    if (!sonos) return;
    const call = (svc, extra = {}) =>
      hass.callService('media_player', svc, { entity_id: sonos, ...extra });

    switch (action) {
      case 'play':       call('media_play'); break;
      case 'pause':      call('media_pause'); break;
      case 'play_pause': call('media_play_pause'); break;
      case 'next':       call('media_next_track'); break;
      case 'prev':       call('media_previous_track'); break;
      case 'shuffle':    call('shuffle_set', { shuffle: !!value }); break;
      case 'repeat':     call('repeat_set',  { repeat: value || 'off' }); break;
      case 'seek':       call('media_seek',  { seek_position: Number(value) }); break;
      case 'mute':       call('volume_mute', { is_volume_muted: !!value }); break;
      case 'join':       call('join',        { group_members: value || [] }); break;
      case 'unjoin':     call('unjoin'); break;
      default:
        console.warn('[ha-controller] sonosCommand(): unknown action', { roomId, action, value });
    }
  }, [hass]);

  // Apple-TV keyboard text services live in the `apple_tv` domain and
  // require a config_entry_id (not an entity_id), so we resolve it once
  // per entity via the entity registry WS endpoint and cache. The
  // resolver runs lazily on first send to avoid a startup round-trip.
  const configEntryCache = React.useRef(new Map());
  const resolveConfigEntry = useCallback(async (entity_id) => {
    if (!hass) return null;
    const cache = configEntryCache.current;
    if (cache.has(entity_id)) return cache.get(entity_id);
    try {
      const result = await hass.callWS({ type: 'config/entity_registry/get', entity_id });
      const id = result?.config_entry_id || null;
      cache.set(entity_id, id);
      return id;
    } catch (e) {
      console.error('[ha-controller] resolveConfigEntry failed', entity_id, e);
      return null;
    }
  }, [hass]);

  // Audio controls (centre / bass / treble / surround). Routes to the
  // right service based on the entity's domain — `number.set_value` for
  // anything in the `number` domain (Sonos tone, Onkyo surround_level),
  // `input_number.set_value` for the DRX centre-level helpers.
  const audioCommand = useCallback((roomId, control, value) => {
    if (!hass) return;
    const cfg = MAPPINGS[roomId]?.audio;
    if (!cfg) return;
    const entity_id = cfg[control];
    if (!entity_id) return;
    const domain = entity_id.split('.')[0];
    if (domain !== 'number' && domain !== 'input_number') {
      console.warn('[ha-controller] audioCommand(): unsupported domain', { roomId, control, entity_id });
      return;
    }
    hass.callService(domain, 'set_value', { entity_id, value: Number(value) });
  }, [hass]);

  // EQ controls (bass / treble), routed to the room's EQ owner. Same
  // service split as audioCommand: `number.set_value` for Sonos tone,
  // `input_number.set_value` for the amp rooms, where the helper is the
  // HA-side source of truth and ha-av's apply automation pushes it to
  // the amp over eISCP. The panel deliberately never touches
  // shell_command directly — it writes a helper and lets ha-av own the
  // wire protocol, the same division the centre-level slider uses.
  const eqCommand = useCallback((roomId, control, value) => {
    if (!hass) return;
    const cfg = MAPPINGS[roomId]?.eq;
    if (!cfg) return;
    const entity_id = cfg[control];
    if (!entity_id) return;
    const domain = entity_id.split('.')[0];
    if (domain !== 'number' && domain !== 'input_number') {
      console.warn('[ha-controller] eqCommand(): unsupported domain', { roomId, control, entity_id });
      return;
    }
    hass.callService(domain, 'set_value', { entity_id, value: Number(value) });
  }, [hass]);

  // Apple-TV keyboard text. Action is one of:
  //   'set'    (value: string)     — replace the field contents
  //   'append' (value: string)     — type more characters
  //   'clear'                      — empty the field
  // Fires apple_tv.{set|append|clear}_keyboard_text on the room's
  // Apple-TV config entry. No-ops if no Apple TV is mapped or if the
  // config entry can't be resolved. Resolves once HA has answered, so
  // the keyboard bar can send one call at a time. HA refuses the call
  // when no text field is focused, which is routine while the bar is
  // in its post-focus grace, so a refusal is logged, not thrown.
  const appleTvText = useCallback(async (roomId, action, value) => {
    if (!hass) return;
    const entity = MAPPINGS[roomId]?.screen?.appleTv;
    if (!entity) return;
    const config_entry_id = await resolveConfigEntry(entity);
    if (!config_entry_id) return;
    const service = { set: 'set_keyboard_text', append: 'append_keyboard_text', clear: 'clear_keyboard_text' }[action];
    if (!service) {
      console.warn('[ha-controller] appleTvText(): unknown action', { roomId, action, value });
      return;
    }
    const data = action === 'clear' ? { config_entry_id } : { config_entry_id, text: String(value ?? '') };
    try {
      await hass.callService('apple_tv', service, data);
    } catch (e) {
      console.warn('[ha-controller] appleTvText(): refused', { roomId, action }, e?.message || e);
    }
  }, [hass, resolveConfigEntry]);

  // Apple-TV transport commands on `media_player.apple_tv_<room>`.
  // Mirrors sonosCommand's surface for the subset of actions the
  // Screen tile actually exercises (play/pause + seek). The full
  // navigational remote stays on the Remote modal via sendRemoteCommand.
  const appleTvCommand = useCallback((roomId, action, value) => {
    if (!hass) return;
    const appleTv = MAPPINGS[roomId]?.screen?.appleTv;
    if (!appleTv) return;
    const call = (svc, extra = {}) =>
      hass.callService('media_player', svc, { entity_id: appleTv, ...extra });

    switch (action) {
      case 'play':       call('media_play'); break;
      case 'pause':      call('media_pause'); break;
      case 'play_pause': call('media_play_pause'); break;
      case 'seek':       call('media_seek', { seek_position: Number(value) }); break;
      default:
        console.warn('[ha-controller] appleTvCommand(): unknown action', { roomId, action, value });
    }
  }, [hass]);

  return {
    rooms, active, activeId, setActiveId,
    update, updateLight, updateUtility, act, runScene, sendRemoteCommand, sonosCommand, sonosSpeakerCommand,
    appleTvCommand, appleTvText, audioCommand, eqCommand, togglePoolExtension,
    browseSonos, playMedia, getSonosQueue, mediaPlayer, louvreCommand, destinationsCommand, coverCommand,
    speakers: overlaySpeakers(haStates),
    poolExtension: overlayPoolExtension(haStates),
  };
}
